import type {RootState} from '@containers/App/store'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns'
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {normalizeDateForPicker} from '@shared/utils/dateUtils'
import type {Locale} from 'date-fns'
import {de, enGB} from 'date-fns/locale'
import type {BaseSyntheticEvent, FC} from 'react'
import {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'

const locales = {
  'en-us': undefined,
  'en-gb': enGB,
  de,
}

type LocaleKey = keyof typeof locales

// The date-fns locale of a picker for the user's preferred locale.
export const getPickerLocale = (localeLanguage: string | undefined): Locale | undefined => {
  switch (localeLanguage) {
    case 'de':
      return locales.de
    case 'en-gb':
      return locales['en-gb']
    default:
      return locales['en-us']
  }
}

interface SingleDateTimeSelectorProps {
  timeSelector: string
  defaultValue: Date | string | null
  onDateChange: (date: Date | null, selector: string) => void
  label: string
  id: string
  name?: string
  error?: boolean
  helperText?: string
  focused?: boolean
  onFocusChanged: (event: BaseSyntheticEvent | string) => void
  minDate?: Date
  maxDate?: Date
}

const SingleDateTimeSelector: FC<SingleDateTimeSelectorProps> = ({
  timeSelector,
  defaultValue,
  onDateChange,
  label,
  id,
  name,
  error,
  helperText,
  focused,
  onFocusChanged,
  minDate,
  maxDate,
}) => {
  // Redux
  const localeLanguage = useSelector((state: RootState) => state.user.preferredLocale)

  // States
  const [locale, setLocale] = useState<LocaleKey>('en-us')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [focusedState, setFocusedState] = useState<boolean>(focused || false)

  // Effects
  useEffect(() => {
    switch (localeLanguage) {
      case 'en':
        setLocale('en-us')
        break
      case 'de':
        setLocale('de')
        break
      case 'en-gb':
        setLocale('en-gb')
        break
      default:
        setLocale('en-us')
    }
  }, [localeLanguage])

  useEffect(() => {
    if (focused) {
      setFocusedState(true)
    } else {
      setFocusedState(false)
    }
  }, [focused])

  // Functions
  const handleDateChange = (date: Date | null | unknown): void => {
    const dateValue = date as Date | null
    if (dateValue !== null && dateValue.toString() !== 'Invalid Date') {
      onDateChange(dateValue, id)
    }
  }

  const handleAcceptChange = (date: Date | null | unknown): void => {
    const dateValue = date as Date | null
    if (dateValue !== null && dateValue.toString() !== 'Invalid Date') {
      onDateChange(dateValue, id)
    }
  }

  const handleSetFocus = (event: BaseSyntheticEvent): void => {
    setFocusedState(true)
    onFocusChanged?.(event)
  }

  const handleDateTimeOpen = (componentId: string): void => {
    setFocusedState(true)
    onFocusChanged?.(componentId)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[locale]}>
      <DateTimePicker
        label={label}
        value={normalizeDateForPicker(defaultValue)}
        onChange={handleDateChange}
        onAccept={handleAcceptChange}
        onOpen={() => handleDateTimeOpen(id as string)}
        {...(minDate && {minDateTime: minDate})}
        {...(maxDate && {maxDateTime: maxDate})}
        slotProps={{
          textField: {
            'data-testid': 'mbai-single-date-time-selector',
            'data-cy': 'mbai-single-date-time-selector',
            id: id,
            onClick: handleSetFocus,
            name: name,
            className: timeSelector === 'start' ? 'active' : '',
            error: error,
            helperText: helperText,
            size: 'small',
            sx: {
              '& .MuiSvgIcon-root': {mr: '0 !important'},
              '& .MuiInputAdornment-root': {mr: '4px !important'},
            },
          },
          actionBar: {
            actions: ['accept', 'cancel'],
          },
        }}
      />
    </LocalizationProvider>
  )
}

export default SingleDateTimeSelector
